import {
  DeepPartial,
  orderInfoSchema,
  OrderInfo as OrderInfoType,
} from "@jasper/client-models";
import { OrderInfoForm, validateSchema } from "@jasper/forms";
import { useOrderWorkflow, StepNavigation } from "@jasper/product-workflows";
import { useGetDentists, useOrderConfiguration } from "@jasper/use-cases";
import { Stack, Title } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useForm } from "@tanstack/react-form";
import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useAuthContext } from "../../../../shared/contexts/AuthContext";
import { UserType } from "/@/generated/graphql";
import { notifications } from "@mantine/notifications";

export const OrderInfo = () => {
  const { user } = useAuthContext();

  const { orderId } = useOutletContext<{ orderId: string }>();
  const { order, isLoading, isEditable, error, updateOrderInfo } =
    useOrderConfiguration({
      orderId,
    });
  const { goNext } = useOrderWorkflow({ orderId });

  const [dentistSearch, setDentistSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(dentistSearch, 300);
  const { dentists, loading: dentistsLoading } = useGetDentists({
    search: debouncedSearch,
  });

  const dentistOptions = useMemo(() => {
    const options = dentists.map(d => ({
      value: d.id,
      label: `Dr. ${d.firstName ?? ""} ${d.lastName ?? ""}${
        d.userGroup?.name ? ` - ${d.userGroup.name}` : ""
      }`,
      name: `${d.firstName ?? ""} ${d.lastName ?? ""}${
        d.userGroup?.name ? ` - ${d.userGroup.name}` : ""
      }`,
    }));

    if (
      user?.type === UserType.Dentist &&
      !options.some(o => o.value === user.id)
    ) {
      options.unshift({
        value: user.id,
        label: `Dr. ${user.firstName ?? ""} ${user.lastName ?? ""}`,
        name: `${user.firstName ?? ""} ${user.lastName ?? ""}`,
      });
    }

    if (
      order?.dentist?.id &&
      !options.some(o => o.value === order.dentist!.id)
    ) {
      options.unshift({
        value: order.dentist.id,
        label: `Dr. ${order.dentist.firstName ?? ""} ${order.dentist.lastName ?? ""}`,
        name: `${order.dentist.firstName ?? ""} ${order.dentist.lastName ?? ""}`,
      });
    }

    return options;
  }, [dentists, user, order]);

  const defaultValues = useMemo<{ orderInfo: DeepPartial<OrderInfoType> }>(
    () => ({
      orderInfo: {
        dentistId: order?.dentist?.id ?? "",
        patient: {
          firstName: order?.patient?.firstName ?? "",
          lastName: order?.patient?.lastName ?? "",
          birthDate: order?.patient?.birthDate ?? undefined,
          sex: order?.patient?.sex ?? undefined,
        },
      },
    }),
    [order]
  );

  const form = useForm({
    defaultValues,
    onSubmit: async values => {
      try {
        await updateOrderInfo(values.value.orderInfo as OrderInfoType);
      } catch {
        notifications.show({
          title: "Erreur",
          message:
            "Une erreur est survenue lors de la sauvegarde des informations de la commande. Veuillez réessayer.",
          color: "red",
        });
        return;
      }

      goNext();
    },
  });

  useEffect(() => {
    if (order) form.reset({ orderInfo: defaultValues.orderInfo });
  }, [order, defaultValues, form]);

  if (isLoading || !order || error || !isEditable) {
    return null;
  }

  return (
    <Stack pos="relative" p="md" maw={700} mx="auto" justify="space-between">
      <Stack gap="md">
        <Title variant="display" size="lg" pb="s6" pt="s12">
          Pour qui cette commande ?
        </Title>
        <form.Field
          name="orderInfo"
          validators={{
            onSubmit: values => {
              const result = validateSchema(orderInfoSchema, values.value);
              if (result.success === true) {
                return undefined;
              }

              return result.errors;
            },
          }}
        >
          {field => {
            return (
              <OrderInfoForm
                value={field.state.value}
                onChange={next => field.setValue(next)}
                dentistOptions={dentistOptions}
                isDentistsLoading={dentistsLoading}
                onDentistSearchChange={setDentistSearch}
                errors={field.state.meta.errorMap.onSubmit}
              />
            );
          }}
        </form.Field>
      </Stack>
      <StepNavigation onNext={() => form.handleSubmit()} />
    </Stack>
  );
};
